import { formatNumberBasedOnUser } from "utils/namesManipulation";

import type { EnergyAnalysisRow } from "../model/types";

import { collectYears, sumValues } from "./aggregate";
import { buildCategoryColorMap } from "./colors";
import { toEnergyAnalysisLabel } from "./labels";

type JointChartInput = {
  co2Rows: EnergyAnalysisRow[];
  co2Unit: string;
  energyRows: EnergyAnalysisRow[];
  energyUnit: string;
};

type PairTotal = {
  co2: number;
  energy: number;
  key: string;
  label: string;
};

const pairKey = (row: EnergyAnalysisRow) => `${row.useType}::${row.utility}`;
const pairLabel = (useType: string, utility: string) =>
  `${toEnergyAnalysisLabel(useType)} · ${toEnergyAnalysisLabel(utility)}`;

const buildPairTotals = (energyRows: EnergyAnalysisRow[], co2Rows: EnergyAnalysisRow[]) => {
  const rows = [...energyRows, ...co2Rows];
  const pairs = new Map<string, Pick<PairTotal, "key" | "label">>();
  rows.forEach((row) => {
    const key = pairKey(row);
    pairs.set(key, {
      key,
      label: pairLabel(row.useType, row.utility),
    });
  });
  return [...pairs.values()]
    .map<PairTotal>((pair) => ({
      ...pair,
      co2: sumValues(co2Rows.filter((row) => pairKey(row) === pair.key)),
      energy: sumValues(energyRows.filter((row) => pairKey(row) === pair.key)),
    }))
    .filter((pair) => pair.energy > 0 || pair.co2 > 0)
    .sort((left, right) => right.energy - left.energy);
};

const maxPairShareByYear = (energyRows: EnergyAnalysisRow[], co2Rows: EnergyAnalysisRow[]) => {
  const years = collectYears([...energyRows, ...co2Rows]);
  return Math.max(
    ...years.flatMap((year) => {
      const yearEnergy = energyRows.filter((row) => row.year === year);
      const yearCo2 = co2Rows.filter((row) => row.year === year);
      const energyTotal = sumValues(yearEnergy);
      const co2Total = sumValues(yearCo2);
      return buildPairTotals(yearEnergy, yearCo2).flatMap((pair) => [
        percent(pair.energy, energyTotal),
        percent(pair.co2, co2Total),
      ]);
    }),
    1
  );
};

const percent = (value: number, total: number) => (total > 0 ? (value / total) * 100 : 0);
const axisNumber = (value: number) => formatNumberBasedOnUser(Math.abs(value), 2);
const buildNiceAxisDomain = (value: number) => {
  const safeValue = Math.max(value, 1);
  const roughInterval = safeValue / 5;
  const magnitude = 10 ** Math.floor(Math.log10(roughInterval));
  const normalized = roughInterval / magnitude;
  const intervalMultiplier = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const interval = intervalMultiplier * magnitude;
  return { interval, max: Math.ceil(safeValue / interval) * interval };
};
export const buildEnergyCo2ButterflyOption = (
  { co2Rows, co2Unit, energyRows, energyUnit }: JointChartInput,
  rangeInput: JointChartInput
) => {
  const energyTotal = sumValues(energyRows);
  const co2Total = sumValues(co2Rows);
  const colors = buildCategoryColorMap(["energy", "co2"]);
  const rangeEnergyTotal = sumValues(rangeInput.energyRows);
  const rangeCo2Total = sumValues(rangeInput.co2Rows);
  const stablePairs = buildPairTotals(rangeInput.energyRows, rangeInput.co2Rows)
    .sort(
      (left, right) =>
        percent(right.energy, rangeEnergyTotal) +
        percent(right.co2, rangeCo2Total) -
        percent(left.energy, rangeEnergyTotal) -
        percent(left.co2, rangeCo2Total)
    )
    .slice(0, 10);
  const selectedPairs = new Map(
    buildPairTotals(energyRows, co2Rows).map((pair) => [pair.key, pair])
  );
  const pairs = stablePairs
    .map((stablePair) => selectedPairs.get(stablePair.key) ?? { ...stablePair, co2: 0, energy: 0 })
    .reverse();
  const domain = buildNiceAxisDomain(maxPairShareByYear(rangeInput.energyRows, rangeInput.co2Rows));
  return {
    dataZoom: [],
    series: [
      {
        barGap: "20%",
        barMaxWidth: 18,
        data: pairs.map((pair) => ({
          raw: pair.energy,
          value: percent(pair.energy, energyTotal),
        })),
        itemStyle: { color: colors.energy },
        name: `Energy (${energyUnit})`,
        tooltip: {
          valueFormatter: (value: number) => `${axisNumber(value)}%`,
        },
        type: "bar",
      },
      {
        barMaxWidth: 18,
        data: pairs.map((pair) => ({
          intensity: pair.energy > 0 ? pair.co2 / pair.energy : 0,
          raw: pair.co2,
          value: percent(pair.co2, co2Total),
        })),
        itemStyle: { color: colors.co2 },
        name: `CO₂ (${co2Unit})`,
        tooltip: {
          valueFormatter: (value: number) => `${axisNumber(value)}%`,
        },
        type: "bar",
      },
    ],
    tooltip: {
      formatter: ({ data, seriesName }: { data: { raw: number }; seriesName: string }) =>
        `${seriesName}<br/>${axisNumber(data.raw)}`,
      trigger: "item",
    },
    xAxis: [
      {
        axisLabel: { formatter: (value: number) => `${axisNumber(value)}%` },
        interval: domain.interval,
        max: domain.max,
        min: 0,
        type: "value",
      },
    ],
    yAxis: [
      {
        axisLabel: { overflow: "truncate", width: 210 },
        axisLine: { show: false },
        axisTick: { show: false },
        boundaryGap: true,
        data: pairs.map((pair) => pair.label),
        type: "category",
      },
    ],
  };
};

export const buildEnergyCo2TrendOption = ({
  co2Rows,
  co2Unit,
  energyRows,
  energyUnit,
}: JointChartInput) => {
  const years = collectYears([...energyRows, ...co2Rows]);
  const colors = buildCategoryColorMap(["energy", "co2"]);
  return {
    dataZoom: [],
    series: [
      {
        data: years.map((year) => sumValues(energyRows.filter((row) => row.year === year))),
        itemStyle: { color: colors.energy },
        name: `Energy (${energyUnit})`,
        symbol: "circle",
        type: "line",
        yAxisIndex: 0,
      },
      {
        data: years.map((year) => sumValues(co2Rows.filter((row) => row.year === year))),
        itemStyle: { color: colors.co2 },
        lineStyle: { type: "dashed" },
        name: `CO₂ (${co2Unit})`,
        symbol: "circle",
        type: "line",
        yAxisIndex: 1,
      },
    ],
    tooltip: { trigger: "axis" },
    xAxis: [{ boundaryGap: false, data: years, type: "category" }],
    yAxis: [
      { axisLabel: { formatter: axisNumber }, name: energyUnit, type: "value" },
      { axisLabel: { formatter: axisNumber }, name: co2Unit, type: "value" },
    ],
  };
};

export const buildEnergyCo2ShareScatterOption = (
  { co2Rows, energyRows }: JointChartInput,
  rangeInput: JointChartInput
) => {
  const years = collectYears([...energyRows, ...co2Rows]);
  const year = years[years.length - 1];
  const yearEnergy = energyRows.filter((row) => row.year === year);
  const yearCo2 = co2Rows.filter((row) => row.year === year);
  const stablePairs = buildPairTotals(rangeInput.energyRows, rangeInput.co2Rows);
  const selectedPairs = new Map(
    buildPairTotals(yearEnergy, yearCo2).map((pair) => [pair.key, pair])
  );
  const pairs = stablePairs.map(
    (stablePair) => selectedPairs.get(stablePair.key) ?? { ...stablePair, co2: 0, energy: 0 }
  );
  const energyTotal = sumValues(yearEnergy);
  const co2Total = sumValues(yearCo2);
  const maxEnergy = Math.max(...pairs.map((pair) => pair.energy), 1);
  const colors = buildCategoryColorMap(pairs.map((pair) => pair.key));
  const points = pairs.map((pair) => ({
    itemStyle: { color: colors[pair.key] },
    name: pair.label,
    symbolSize: 7 + 13 * Math.sqrt(pair.energy / maxEnergy),
    value: [percent(pair.energy, energyTotal), percent(pair.co2, co2Total)],
  }));
  const domain = buildNiceAxisDomain(maxPairShareByYear(rangeInput.energyRows, rangeInput.co2Rows));
  return {
    dataZoom: [],
    grid: { left: 60 },
    series: [
      {
        clip: false,
        data: points,
        emphasis: { focus: "self" },
        type: "scatter",
      },
      {
        data: [
          [0, 0],
          [domain.max, domain.max],
        ],
        lineStyle: { color: "#687086", type: "dashed", width: 1 },
        showSymbol: false,
        silent: true,
        type: "line",
      },
    ],
    tooltip: {
      formatter: ({ data }: { data: { name: string; value: number[] } }) =>
        `${data.name}<br/>Energy: ${axisNumber(data.value[0] ?? 0)}%<br/>CO₂: ${axisNumber(data.value[1] ?? 0)}%`,
      trigger: "item",
    },
    xAxis: [
      {
        axisLabel: { formatter: axisNumber },
        interval: domain.interval,
        max: domain.max,
        min: 0,
        name: `% of energy (${year ?? ""})`,
        nameGap: 25,
        nameLocation: "center",
        type: "value",
      },
    ],
    yAxis: [
      {
        axisLabel: { formatter: axisNumber },
        interval: domain.interval,
        max: domain.max,
        min: 0,
        name: `% of CO₂ (${year ?? ""})`,
        nameGap: 50,
        nameLocation: "center",
        nameRotate: 90,
        type: "value",
      },
    ],
  };
};

export const buildEnergyCo2YearlyScatterOption = ({
  co2Rows,
  co2Unit,
  energyRows,
  energyUnit,
}: JointChartInput) => {
  const years = collectYears([...energyRows, ...co2Rows]);
  const points = years.map((year) => ({
    label: { formatter: `${year}`, position: "top", show: true },
    name: `${year}`,
    value: [
      sumValues(energyRows.filter((row) => row.year === year)),
      sumValues(co2Rows.filter((row) => row.year === year)),
    ],
  }));
  return {
    dataZoom: [],
    grid: { left: 80 },
    series: [
      {
        clip: false,
        data: points,
        itemStyle: { color: "#3f748c" },
        labelLayout: { hideOverlap: true },
        symbolSize: 9,
        type: "scatter",
      },
    ],
    tooltip: {
      formatter: ({ data }: { data: { name: string; value: number[] } }) =>
        `${data.name}<br/>${axisNumber(data.value[0] ?? 0)} ${energyUnit}<br/>${axisNumber(data.value[1] ?? 0)} ${co2Unit}`,
      trigger: "item",
    },
    xAxis: [
      {
        axisLabel: { formatter: axisNumber },
        name: `Energy (${energyUnit})`,
        nameGap: 25,
        nameLocation: "center",
        type: "value",
      },
    ],
    yAxis: [
      {
        axisLabel: { formatter: axisNumber },
        name: `CO₂ (${co2Unit})`,
        nameGap: 82,
        nameLocation: "center",
        nameRotate: 90,
        type: "value",
      },
    ],
  };
};
